import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/testIds";
import { toast } from "sonner";
import { LogOut, UserCircle, ShieldCheck, Settings } from "lucide-react";

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [coachesCanAdd, setCoachesCanAdd] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getSettings().then((s) => setCoachesCanAdd(s.coaches_can_add_students)).catch(() => {});
  }, []);

  const toggle = async (val) => {
    setCoachesCanAdd(val);
    setSaving(true);
    try {
      await api.updateSettings({ coaches_can_add_students: val });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.message);
      setCoachesCanAdd(!val);
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => {
    logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">Profile</h1>

      <div className="flex items-center gap-4 bg-slate-50 rounded-xl border border-slate-100 p-6">
        <div className="w-16 h-16 rounded-full bg-[#0A2540] text-white flex items-center justify-center font-extrabold text-2xl">
          {user?.full_name?.charAt(0)?.toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-slate-900 truncate">{user?.full_name}</p>
          <p className="text-sm text-slate-500 truncate">{user?.phone_number}</p>
          <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wider text-[#FF6B6B]">
            <ShieldCheck className="w-3 h-3" />
            {user?.role}
          </span>
        </div>
      </div>

      {isAdmin && (
        <div className="bg-slate-50 rounded-xl border border-slate-100 p-6 space-y-4">
          <div className="flex items-center gap-2 text-slate-900">
            <Settings className="w-4 h-4" />
            <h2 className="font-bold">Settings</h2>
          </div>
          <label className="flex items-center justify-between gap-4 py-2 border-t border-slate-100">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">Allow coaches to add students</p>
              <p className="text-xs text-slate-500">
                When on, coaches see the + button and can create students. Edit/delete stays admin-only.
              </p>
            </div>
            <input
              data-testid={TID.settingsToggle}
              type="checkbox"
              checked={coachesCanAdd}
              disabled={saving}
              onChange={(e) => toggle(e.target.checked)}
              className="w-5 h-5 accent-[#0A2540] shrink-0"
            />
          </label>
        </div>
      )}

      {!isAdmin && <CoachAttendanceThisMonth />}

      <button
        onClick={doLogout}
        data-testid={`${TID.logoutButton}-profile`}
        className="w-full flex items-center justify-center gap-2 h-12 border border-red-200 text-red-600 rounded-lg font-semibold hover:bg-red-50"
      >
        <LogOut className="w-4 h-4" />
        Log out
      </button>
    </div>
  );
}

function CoachAttendanceThisMonth() {
  const [state, setState] = useState({ loading: true, dates: [], month: "" });
  useEffect(() => {
    let alive = true;
    api
      .myCoachAttendanceDates()
      .then((r) => alive && setState({ loading: false, dates: r.dates, month: r.month }))
      .catch(() => alive && setState({ loading: false, dates: [], month: "" }));
    return () => {
      alive = false;
    };
  }, []);
  const monthLabel = state.month
    ? new Date(state.month + "-01").toLocaleString("en-IN", { month: "long", year: "numeric" })
    : "";
  return (
    <div className="bg-slate-50 rounded-xl border border-slate-100 p-6 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
          My Attendance · {monthLabel}
        </p>
        <span className="text-xs font-semibold text-slate-500">
          {state.loading ? "..." : `${state.dates.length} day${state.dates.length === 1 ? "" : "s"}`}
        </span>
      </div>
      {state.loading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : state.dates.length === 0 ? (
        <p className="text-sm text-slate-500">No approved attendance this month yet.</p>
      ) : (
        <ul
          data-testid="coach-attendance-dates"
          className="flex flex-wrap gap-1.5"
        >
          {state.dates.map((d) => (
            <li
              key={d}
              className="px-2 py-1 text-xs font-semibold text-[#0A8F3D] bg-[#34C75922] rounded-md"
            >
              {d}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
